import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Typography,
  IconButton,
  Select,
  MenuItem,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Tabs,
  Tab,
  CircularProgress,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import { marketingPersonApi, MarketingPerson, OrganizerAssignment } from '../../../modules/services/marketingPersonService';
import { useAppSelector } from '../../../modules/store/hooks';
import translate from '../../../i18n/translate';
import AddAssignmentModal from '../AddAssignmentModal';

interface EditMarketingPersonModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  person: MarketingPerson;
}

const EditMarketingPersonModal: React.FC<EditMarketingPersonModalProps> = ({ open, onClose, onSuccess, person }) => {
  const language = useAppSelector((state) => state.auth.language);
  const lang = language?.split('-')?.[0] || 'en';

  const [isDisabled, setIsDisabled] = useState(person.isDisabled);
  const [loading, setLoading] = useState(false);
  const [assignmentTab, setAssignmentTab] = useState<string>('all');

  // Editing assignment state
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editCommissionValue, setEditCommissionValue] = useState('');
  const [editCommissionType, setEditCommissionType] = useState<'fixed' | 'percent'>('percent');
  const [editStartDate, setEditStartDate] = useState('');
  const [editEndDate, setEditEndDate] = useState('');
  const [editIsDisabled, setEditIsDisabled] = useState(false);

  // New assignment modal
  const [addAssignmentModalOpen, setAddAssignmentModalOpen] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);

  const { data: assignments = [], isLoading: assignmentsLoading, refetch: refetchAssignments } =
    marketingPersonApi.useGetPersonAssignmentsQuery(
      { marketingPersonId: person.id, status: assignmentTab },
      { skip: !open }
    );

  useEffect(() => {
    setIsDisabled(person.isDisabled);
  }, [person]);

  const [updatePerson] = marketingPersonApi.useUpdateMarketingPersonMutation();
  const [updateAssignment] = marketingPersonApi.useUpdateOrganizerAssignmentMutation();
  const [removeAssignment] = marketingPersonApi.useRemoveOrganizerAssignmentMutation();

  const handleAddAssignmentSuccess = (_newAssignment: OrganizerAssignment) => {
    refetchAssignments();
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await updatePerson({
        id: person.id,
        body: { isDisabled },
        language: lang,
      }).unwrap();
      onSuccess();
    } catch (err) {
      console.error('Failed to update marketing person', err);
    } finally {
      setLoading(false);
    }
  };

  const handleStartEdit = (a: OrganizerAssignment) => {
    setEditingId(a.id);
    setEditCommissionValue(String(a.commissionValue));
    setEditCommissionType(a.commissionType);
    setEditStartDate(a.startDate ? a.startDate.split('T')[0] : '');
    setEditEndDate(a.endDate ? a.endDate.split('T')[0] : '');
    setEditIsDisabled(a.isDisabled);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
  };

  const handleSaveAssignment = async (assignmentId: number) => {
    try {
      await updateAssignment({
        assignmentId,
        body: {
          commissionValue: Number(editCommissionValue) || 0,
          commissionType: editCommissionType,
          startDate: editStartDate || undefined,
          endDate: editEndDate || undefined,
          isDisabled: editIsDisabled,
        },
        language: lang,
      }).unwrap();
      refetchAssignments();
      setEditingId(null);
    } catch (err) {
      console.error('Failed to update assignment', err);
    }
  };

  const handleRemoveAssignment = async (assignmentId: number) => {
    try {
      await removeAssignment({ assignmentId, language: lang }).unwrap();
      refetchAssignments();
      setDeleteConfirmId(null);
    } catch (err) {
      console.error('Failed to remove assignment', err);
    }
  };

  const handleClose = () => {
    setEditingId(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>{translate('marketingPersonsEditTitle')}: {person.name || person.phone}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
        <TextField
          label={translate('marketingPersonsPhoneNumber')}
          value={person.phone}
          disabled
          fullWidth
          margin="dense"
        />
        <FormControlLabel
          control={<Switch checked={isDisabled} onChange={(e) => setIsDisabled(e.target.checked)} color="warning" />}
          label={translate('marketingPersonsDisabled')}
        />

        <Divider sx={{ my: 1 }} />
        <Typography variant="subtitle1" color="warning.main" fontWeight="bold">
          {translate('marketingPersonsGridAssignments') || 'Organizer Assignments'}
          {!assignmentsLoading && <span> ({assignments.length})</span>}
        </Typography>

        <Tabs
          value={assignmentTab}
          onChange={(_, v) => setAssignmentTab(v)}
          sx={{ mb: 1, '& .MuiTab-root': { color: 'warning.main' }, '& .Mui-selected': { color: 'warning.dark' } }}
          TabIndicatorProps={{ sx: { backgroundColor: 'warning.main' } }}
        >
          <Tab label={translate('marketingPersonsAssignmentsAll')} value="all" />
          <Tab label={translate('marketingPersonsAssignmentsActive')} value="active" />
          <Tab label={translate('marketingPersonsAssignmentsUnpaid')} value="unpaid" />
          <Tab label={translate('marketingPersonsAssignmentsPaid')} value="paid" />
        </Tabs>

        {assignmentsLoading ? (
          <CircularProgress color="warning" size={24} />
        ) : assignments.length > 0 && (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{translate('marketingPersonsGridOrganizer')}</TableCell>
                  <TableCell>{translate('marketingPersonsGridCommission')}</TableCell>
                  <TableCell>{translate('marketingPersonsGridFee')}</TableCell>
                  <TableCell>{translate('marketingPersonsGridStartDate')}</TableCell>
                  <TableCell>{translate('marketingPersonsGridEndDate')}</TableCell>
                  <TableCell>{translate('marketingPersonsGridStatus')}</TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {assignments.map((a: OrganizerAssignment) => (
                  editingId === a.id ? (
                    <TableRow key={a.id}>
                      <TableCell>{a.organizerName} (ID: {a.organizerId})</TableCell>
                      <TableCell>
                        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                          <TextField
                            type="number"
                            size="small"
                            value={editCommissionValue}
                            onChange={(e) => setEditCommissionValue(e.target.value)}
                            sx={{ width: 70 }}
                          />
                          <Select
                            size="small"
                            value={editCommissionType}
                            onChange={(e) => setEditCommissionType(e.target.value as any)}
                            sx={{ width: 90 }}
                          >
                            <MenuItem value="percent">%</MenuItem>
                          </Select>
                        </div>
                      </TableCell>
                      <TableCell>
                        <TextField
                          type="date"
                          size="small"
                          InputLabelProps={{ shrink: true }}
                          value={editStartDate}
                          onChange={(e) => setEditStartDate(e.target.value)}
                          sx={{ width: 140 }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          type="date"
                          size="small"
                          InputLabelProps={{ shrink: true }}
                          value={editEndDate}
                          onChange={(e) => setEditEndDate(e.target.value)}
                          sx={{ width: 140 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Switch
                          size="small"
                          checked={editIsDisabled}
                          onChange={(e) => setEditIsDisabled(e.target.checked)}
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton size="small" color="success" onClick={() => handleSaveAssignment(a.id)}>
                          <SaveIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={handleCancelEdit}>
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ) : (
                    <TableRow key={a.id}>
                      <TableCell>{a.organizerName} (ID: {a.organizerId})</TableCell>
                      <TableCell>{a.commissionValue}{a.commissionType === 'percent' ? '%' : ' ₪'}</TableCell>
                      <TableCell>
                        {a.commissionType === 'percent' && a.totalFee != null
                          ? <Typography variant="body2" color="warning.main" fontWeight="bold">₪{a.totalFee.toLocaleString()}</Typography>
                          : '-'}
                      </TableCell>
                      <TableCell>{a.startDate ? new Date(a.startDate).toLocaleDateString() : '-'}</TableCell>
                      <TableCell>{a.endDate ? new Date(a.endDate).toLocaleDateString() : '-'}</TableCell>
                      <TableCell>
                        <Chip
                          label={a.isDisabled ? translate('marketingPersonsStatusDisabled') : translate('marketingPersonsStatusActive')}
                          color={a.isDisabled ? 'default' : 'success'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton size="small" color="warning" onClick={() => handleStartEdit(a)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => setDeleteConfirmId(a.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  )
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Divider sx={{ my: 1 }} />
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => setAddAssignmentModalOpen(true)}
          color="warning"
        >
          {translate('marketingPersonsSearchOrganizer') || 'Search'}
        </Button>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="warning">{translate('marketingPersonsCancel')}</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={loading}
          color="warning"
        >
          {loading ? translate('marketingPersonsSaving') : translate('marketingPersonsSave')}
        </Button>
      </DialogActions>

      <AddAssignmentModal
        open={addAssignmentModalOpen}
        onClose={() => setAddAssignmentModalOpen(false)}
        onSuccess={handleAddAssignmentSuccess}
        marketingPersonId={person.id}
      />

      <Dialog open={deleteConfirmId !== null} onClose={() => setDeleteConfirmId(null)}>
        <DialogTitle color="warning.main">{translate('marketingPersonsConfirmDeleteTitle')}</DialogTitle>
        <DialogContent>
          <Typography>{translate('marketingPersonsConfirmAction')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmId(null)} color="warning">{translate('marketingPersonsCancel')}</Button>
          <Button onClick={() => deleteConfirmId && handleRemoveAssignment(deleteConfirmId)} color="error" variant="contained">{translate('marketingPersonsDelete')}</Button>
        </DialogActions>
      </Dialog>

    </Dialog>
  );
};

export default EditMarketingPersonModal;
